import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from "@angular/core";
import { DxTextAreaModule } from "devextreme-angular";
import notify from "devextreme/ui/notify";
import { GpdButtonComponent } from "src/app/components/gpd-button/gpd-button.component";
import { ContractConfigService } from "src/app/services/contracts/contract-config.service";

import { ActivityLogService } from "src/app/services/activity-log.service";
import { ActivitySection } from "src/app/constants/activity-log";
interface ContractReviewSettings {
  extractPrompt: string;
  createSummaryPrompt: string;
  checkCriteriaPrompt: string;
  criteria: string[];
}

@Component({
    selector: "app-contract-review-settings",
    imports: [
        CommonModule,
        DxTextAreaModule,
        GpdButtonComponent,
    ],
    templateUrl: "./contract-review-settings.component.html",
    styleUrls: ["./contract-review-settings.component.css"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractReviewSettingsComponent implements OnInit {
  settings: ContractReviewSettings = {
    extractPrompt: "",
    createSummaryPrompt: "",
    checkCriteriaPrompt: "",
    criteria: [],
  };

  isLoading = false;
  isSaving = false;

  /** Serialized snapshot of the last loaded/saved state, used to detect changes. */
  private baselineJson = "";

  get hasChanges(): boolean {
    return !this.isLoading && this.currentJson() !== this.baselineJson;
  }

  private currentJson(): string {
    return JSON.stringify(this.settings);
  }

  constructor(
    private contractConfigService: ContractConfigService,
    private cdr: ChangeDetectorRef,
    private activityLog: ActivityLogService
  ) {}

  async ngOnInit(): Promise<void> {
    this.activityLog.logView({ Section: ActivitySection.CONFIG_CONTRACT_REVIEW });
    await this.load();
  }

  async load(): Promise<void> {
    this.isLoading = true;
    const result = await this.contractConfigService.getContractReviewSettings();
    if (result) {
      this.settings = {
        extractPrompt: result.extractPrompt ?? "",
        createSummaryPrompt: result.createSummaryPrompt ?? "",
        checkCriteriaPrompt: result.checkCriteriaPrompt ?? "",
        criteria: result.criteria ?? [],
      };
    }
    this.baselineJson = this.currentJson();
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  addCriterion(): void {
    this.settings.criteria = [...this.settings.criteria, ""];
    this.cdr.markForCheck();
  }

  removeCriterion(index: number): void {
    this.settings.criteria = this.settings.criteria.filter(
      (_, i) => i !== index
    );
    this.cdr.markForCheck();
  }

  updateCriterion(index: number, value: string): void {
    this.settings.criteria[index] = value;
    this.cdr.markForCheck();
  }

  trackByIndex(index: number): number {
    return index;
  }

  async save(): Promise<void> {
    this.isSaving = true;
    const payload: ContractReviewSettings = {
      ...this.settings,
      criteria: this.settings.criteria
        .map((c) => (c ?? "").trim())
        .filter((c) => c.length > 0),
    };

    const success =
      await this.contractConfigService.saveContractReviewSettings(payload);
    if (success) {
      notify(
        $localize`:@@reviewSettingsSaved:Review settings saved.`,
        "success",
        3000
      );
      this.settings.criteria = payload.criteria;
      this.baselineJson = this.currentJson();
    }
    this.isSaving = false;
    this.cdr.markForCheck();
  }
}
